import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, Link2Off, AlertTriangle, X, Users } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

interface Connection {
  id: string;
  status: string;
  created_at: string;
  other_user: {
    id: string;
    full_name: string;
    avatar_url: string | null;
    role: string;
    email?: string;
  };
}

export function ConnectionResetManager() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showConfirmAllDialog, setShowConfirmAllDialog] = useState(false);
  const [showConfirmSingleDialog, setShowConfirmSingleDialog] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null);

  // Search users
  const { data: searchResults, isLoading: searching } = useQuery({
    queryKey: ["admin_user_search_connections", searchQuery],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2) return [];

      // Search profiles by name
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, role")
        .ilike("full_name", `%${searchQuery}%`)
        .limit(10);

      if (profilesError) throw profilesError;

      // Search by email in profile_private
      const { data: privateProfiles, error: privateError } = await supabase
        .from("profile_private")
        .select("user_id, email")
        .ilike("email", `%${searchQuery}%`)
        .limit(10);

      if (privateError) throw privateError;

      // Get profile details for email matches
      const emailUserIds = privateProfiles?.map((p) => p.user_id) || [];
      let emailMatchProfiles: any[] = [];
      if (emailUserIds.length > 0) {
        const { data } = await supabase
          .from("profiles")
          .select("id, full_name, avatar_url, role")
          .in("id", emailUserIds);
        emailMatchProfiles = data || [];
      }

      // Merge and dedupe results
      const allProfiles = [...(profiles || []), ...emailMatchProfiles];
      const uniqueProfiles = allProfiles.filter(
        (profile, index, self) => index === self.findIndex((p) => p.id === profile.id)
      );

      // Attach emails
      const allUserIds = uniqueProfiles.map((p) => p.id);
      let emailMap: Record<string, string> = {};
      if (allUserIds.length > 0) {
        const { data: emailData } = await supabase
          .from("profile_private")
          .select("user_id, email")
          .in("user_id", allUserIds);
        emailData?.forEach((e) => {
          emailMap[e.user_id] = e.email;
        });
      }

      return uniqueProfiles.map((p) => ({
        ...p,
        email: emailMap[p.id] || null,
      }));
    },
    enabled: searchQuery.length >= 2,
  });

  // Get all connections for selected user
  const { data: userConnections, isLoading: connectionsLoading } = useQuery({
    queryKey: ["user_connections_detail", selectedUser?.id],
    queryFn: async () => {
      if (!selectedUser?.id) return [];

      const { data, error } = await supabase
        .from("connections")
        .select("id, status, created_at, requester_id, recipient_id")
        .or(`requester_id.eq.${selectedUser.id},recipient_id.eq.${selectedUser.id}`)
        .order("created_at", { ascending: false });

      if (error) throw error;
      if (!data || data.length === 0) return [];

      // Get all other user IDs
      const otherUserIds = data.map((c) =>
        c.requester_id === selectedUser.id ? c.recipient_id : c.requester_id
      );

      // Fetch other user profiles
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, role")
        .in("id", otherUserIds);

      // Fetch emails
      const { data: emails } = await supabase
        .from("profile_private")
        .select("user_id, email")
        .in("user_id", otherUserIds);

      const profileMap = new Map(profiles?.map((p) => [p.id, p]) || []);
      const emailMap = new Map(emails?.map((e) => [e.user_id, e.email]) || []);

      return data.map((c) => {
        const otherId = c.requester_id === selectedUser.id ? c.recipient_id : c.requester_id;
        const otherProfile = profileMap.get(otherId);
        return {
          id: c.id,
          status: c.status,
          created_at: c.created_at,
          other_user: {
            id: otherId,
            full_name: otherProfile?.full_name || "Unknown",
            avatar_url: otherProfile?.avatar_url || null,
            role: otherProfile?.role || "unknown",
            email: emailMap.get(otherId) || undefined,
          },
        };
      }) as Connection[];
    },
    enabled: !!selectedUser?.id,
  });

  // Reset single connection mutation
  const resetSingleConnection = useMutation({
    mutationFn: async ({ connectionId, otherUser }: { connectionId: string; otherUser: any }) => {
      // Delete the specific connection
      const { error } = await supabase
        .from("connections")
        .delete()
        .eq("id", connectionId);

      if (error) throw error;

      // Delete related contact shares between these two users
      const { error: sharesError } = await supabase
        .from("contact_shares")
        .delete()
        .or(
          `and(owner_id.eq.${selectedUser.id},shared_with_id.eq.${otherUser.id}),and(owner_id.eq.${otherUser.id},shared_with_id.eq.${selectedUser.id})`
        );

      if (sharesError) throw sharesError;

      // Log the action
      const { error: logError } = await supabase.from("admin_activity_logs").insert({
        admin_id: (await supabase.auth.getUser()).data.user?.id,
        action_type: "reset_single_connection",
        target_user_id: selectedUser?.id,
        target_user_email: selectedUser?.email,
        details: {
          connection_id: connectionId,
          other_user_id: otherUser.id,
          other_user_name: otherUser.full_name,
        },
      });

      if (logError) console.error("Failed to log action:", logError);
    },
    onSuccess: () => {
      toast.success(
        `Connection between ${selectedUser?.full_name} and ${selectedConnection?.other_user.full_name} reset`
      );
      queryClient.invalidateQueries({ queryKey: ["user_connections_detail"] });
      queryClient.invalidateQueries({ queryKey: ["user_connections"] });
      queryClient.invalidateQueries({ queryKey: ["pending_requests_admin"] });
      queryClient.invalidateQueries({ queryKey: ["admin_stats"] });
      setShowConfirmSingleDialog(false);
      setSelectedConnection(null);
    },
    onError: (error: any) => {
      toast.error("Failed to reset connection: " + error.message);
    },
  });

  // Reset all connections mutation
  const resetAllConnections = useMutation({
    mutationFn: async (userId: string) => {
      // Delete all connections where user is requester or recipient
      const { error } = await supabase
        .from("connections")
        .delete()
        .or(`requester_id.eq.${userId},recipient_id.eq.${userId}`);

      if (error) throw error;

      // Also delete related contact shares
      const { error: sharesError } = await supabase
        .from("contact_shares")
        .delete()
        .or(`owner_id.eq.${userId},shared_with_id.eq.${userId}`);

      if (sharesError) throw sharesError;

      // Log the action
      const { error: logError } = await supabase.from("admin_activity_logs").insert({
        admin_id: (await supabase.auth.getUser()).data.user?.id,
        action_type: "reset_all_user_connections",
        target_user_id: userId,
        target_user_email: selectedUser?.email,
        details: { connections_deleted: userConnections?.length || 0 },
      });

      if (logError) console.error("Failed to log action:", logError);
    },
    onSuccess: () => {
      toast.success(`All connections reset for ${selectedUser?.full_name}`);
      queryClient.invalidateQueries({ queryKey: ["user_connections_detail"] });
      queryClient.invalidateQueries({ queryKey: ["user_connections"] });
      queryClient.invalidateQueries({ queryKey: ["pending_requests_admin"] });
      queryClient.invalidateQueries({ queryKey: ["admin_stats"] });
      setShowConfirmAllDialog(false);
      setSelectedUser(null);
      setSearchQuery("");
    },
    onError: (error: any) => {
      toast.error("Failed to reset connections: " + error.message);
    },
  });

  const handleSelectUser = (user: any) => {
    setSelectedUser(user);
    setSearchQuery("");
  };

  const handleResetAllClick = () => {
    if (selectedUser && userConnections && userConnections.length > 0) {
      setShowConfirmAllDialog(true);
    } else {
      toast.info("This user has no connections to reset.");
    }
  };

  const handleResetSingleClick = (connection: Connection) => {
    setSelectedConnection(connection);
    setShowConfirmSingleDialog(true);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      pending_admin: "secondary",
      pending_mentor: "secondary",
      accepted: "default",
      completed: "default",
      declined: "destructive",
      rejected_admin: "destructive",
    };
    const labels: Record<string, string> = {
      pending_admin: "Pending Admin",
      pending_mentor: "Pending",
      accepted: "Accepted",
      completed: "Completed",
      declined: "Declined",
      rejected_admin: "Rejected",
    };
    return (
      <Badge variant={variants[status] || "outline"} className="text-xs">
        {labels[status] || status}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="user-search">Search User</Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="user-search"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        {searching && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Searching...
          </div>
        )}
        {searchResults && searchResults.length > 0 && (
          <div className="border rounded-lg divide-y max-h-60 overflow-y-auto bg-background">
            {searchResults.map((user) => (
              <button
                key={user.id}
                onClick={() => handleSelectUser(user)}
                className="w-full p-3 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user.avatar_url || undefined} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {user.full_name?.charAt(0) || "?"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{user.full_name}</p>
                  {user.email && (
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  )}
                </div>
                <Badge variant="outline" className="capitalize text-xs">
                  {user.role}
                </Badge>
              </button>
            ))}
          </div>
        )}
        {searchQuery.length >= 2 && searchResults?.length === 0 && !searching && (
          <p className="text-sm text-muted-foreground">No users found</p>
        )}
      </div>

      {selectedUser && (
        <div className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12">
              <AvatarImage src={selectedUser.avatar_url || undefined} />
              <AvatarFallback className="bg-primary text-primary-foreground">
                {selectedUser.full_name?.charAt(0) || "?"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="font-semibold">{selectedUser.full_name}</p>
              {selectedUser.email && (
                <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
              )}
              <Badge variant="outline" className="capitalize mt-1">
                {selectedUser.role}
              </Badge>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSelectedUser(null)}>
              Clear
            </Button>
          </div>

          {/* Summary and Reset All */}
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div>
              <p className="text-sm font-medium">Total Connections</p>
              <p className="text-2xl font-bold">
                {connectionsLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  userConnections?.length || 0
                )}
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={handleResetAllClick}
              disabled={
                resetAllConnections.isPending || connectionsLoading || !userConnections?.length
              }
            >
              {resetAllConnections.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Link2Off className="h-4 w-4 mr-2" />
              )}
              Reset All Connections
            </Button>
          </div>

          {/* Individual Connections List */}
          {connectionsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : userConnections && userConnections.length > 0 ? (
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">
                Individual Connections ({userConnections.length})
              </Label>
              <div className="border rounded-lg divide-y max-h-80 overflow-y-auto">
                {userConnections.map((connection) => (
                  <div
                    key={connection.id}
                    className="p-3 flex items-center gap-3"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={connection.other_user.avatar_url || undefined} />
                      <AvatarFallback className="bg-muted text-muted-foreground text-xs">
                        {connection.other_user.full_name?.charAt(0) || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        {connection.other_user.full_name}
                      </p>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="capitalize text-xs">
                          {connection.other_user.role}
                        </Badge>
                        {getStatusBadge(connection.status)}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleResetSingleClick(connection)}
                      disabled={resetSingleConnection.isPending}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Reset
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Users className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No connections found</p>
            </div>
          )}
        </div>
      )}

      {!selectedUser && !searchQuery && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Link2Off className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Search for a user to manage their connections</p>
          <p className="text-sm text-muted-foreground mt-1">
            You can reset individual connections or all connections at once
          </p>
        </div>
      )}

      {/* Confirm Reset All Dialog */}
      <AlertDialog open={showConfirmAllDialog} onOpenChange={setShowConfirmAllDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Reset All Connections?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This will permanently delete{" "}
                <strong>{userConnections?.length || 0}</strong> connection(s) for{" "}
                <strong>{selectedUser?.full_name}</strong>.
              </p>
              <p>This includes:</p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>All pending connection requests (sent and received)</li>
                <li>All accepted connections</li>
                <li>All completed connections</li>
                <li>All associated contact sharing permissions</li>
              </ul>
              <p className="font-medium text-destructive">This action cannot be undone.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedUser && resetAllConnections.mutate(selectedUser.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {resetAllConnections.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Reset All Connections
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Reset Single Connection Dialog */}
      <AlertDialog open={showConfirmSingleDialog} onOpenChange={setShowConfirmSingleDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Reset This Connection?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This will permanently delete the connection between{" "}
                <strong>{selectedUser?.full_name}</strong> and{" "}
                <strong>{selectedConnection?.other_user.full_name}</strong>.
              </p>
              <p>This includes:</p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>The connection request ({selectedConnection?.status})</li>
                <li>Contact sharing permissions between these users</li>
              </ul>
              <p className="font-medium text-destructive">This action cannot be undone.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                selectedConnection &&
                resetSingleConnection.mutate({
                  connectionId: selectedConnection.id,
                  otherUser: selectedConnection.other_user,
                })
              }
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {resetSingleConnection.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Reset Connection
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
